import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { cosineGet, cosinePost } from "./default-query"
import { useRouter } from "next/navigation"
import { useAnalytics } from "@/hooks"
import { OAuthCredential, signInWithCustomToken, signOut, User } from "firebase/auth"
import { auth } from "@/lib/firebase"
import { useSupport } from "@/hooks/support"
import { useSearchParams } from "next/navigation"

type LoginProps = {
  idToken: string
  refreshToken?: string
  providerId: OAuthCredential["providerId"]
  oauthAccessToken?: OAuthCredential["accessToken"]
}

type LoginLocalProps = {
  email: string
  password: string
  isSignUp: boolean
}

export const useLogin = (props: { onError: (error: Error) => void }) => {
  const { onError } = props
  const queryClient = useQueryClient()
  const { identify, track } = useAnalytics()
  const router = useRouter()
  const searchParams = useSearchParams()
  const returnTo = searchParams.get("returnTo")

  return useMutation({
    mutationFn: (data: LoginProps) => cosinePost(`/auth/login`, data),
    onSuccess: (data: any) => {
      identify(data.user.id, { email: data.user.email, name: data.user.name })
      track("login")
      queryClient.clear()
      // Note: the check that the returnTo starts with a slash is important - it prevents people redirecting to external sites
      if (returnTo && returnTo.startsWith("/")) {
        router.replace(returnTo)
      } else {
        router.replace("/")
      }
    },
    onError: (error) => onError(error),
  })
}

export const useLoginEmail = (props: { onError: (error: Error) => void }) => {
  const { onError } = props
  const queryClient = useQueryClient()
  const { identify, track } = useAnalytics()
  const router = useRouter()
  const searchParams = useSearchParams()
  const returnTo = searchParams.get("returnTo")

  return useMutation({
    mutationFn: (data: LoginProps) => cosinePost(`/auth/login`, data),
    onSuccess: (data: any) => {
      identify(data.user.id, { email: data.user.email, name: data.user.name })
      track("login by email")
      queryClient.clear()
      // Note: the check that the returnTo starts with a slash is important - it prevents people redirecting to external sites
      if (returnTo && returnTo.startsWith("/")) {
        router.replace(returnTo)
      } else {
        router.replace("/")
      }
    },
    onError: (error) => onError(error),
  })
}

export const useLocalLogin = (props: { onError: (error: Error) => void }) => {
  const { onError } = props
  const queryClient = useQueryClient()
  const router = useRouter()

  return useMutation({
    mutationFn: (data: LoginLocalProps) => {
      if (data.isSignUp) {
        return cosinePost(`/auth/signup`, data)
      }
      return cosinePost(`/auth/login/local`, data)
    },
    onSuccess: () => {
      queryClient.clear()
      router.replace("/")
    },
    onError: (error) => onError(error),
  })
}

export const useLogout = () => {
  const queryClient = useQueryClient()
  const router = useRouter()
  const support = useSupport()
  const { track, reset } = useAnalytics()
  return useMutation({
    mutationFn: () => {
      if (support.chatEnabled) {
        support.clearChat()
      }
      return cosinePost(`/auth/logout`)
    },
    onSuccess: () => {
      signOut(auth)
      track("logout")
      queryClient.clear()
      router.replace("/login")
    },
    onSettled: () => {
      reset()
    },
  })
}

const FirebaseTokenKey = "firebase-token"

export const useFirebaseUser = () => {
  const setUser = (user: User | null) => queryClient.setQueryData([FirebaseTokenKey], user)
  const queryClient = useQueryClient()
  const user = useQuery({
    queryKey: [FirebaseTokenKey],
    queryFn: async () => {
      const response = await cosineGet<{ token: string }>("/auth/token")
      if (!response.token) {
        return null
      }
      return await signInWithCustomToken(auth, response.token).then((res) => res.user)
    },
  })
  return [user.data ?? null, setUser] as const
}
